import {ReduxNormalizedType} from '../app-types';
import {immerable} from 'immer';

export type FacetOnChange = (facetGroup: FacetGroup, newSelectedFacetValues: string[]) => void;

/**
 * Defines whether only a single facet can be selected at a time (SINGLE) or
 * multiple at once (MULTI).
 */
export enum SelectionType {
    SINGLE,
    MULTI,
    HIDDEN,
    RANGE,
}

/**
 * Defines which selection type should be used if none is defined.
 */
export const DEFAULT_SELECTION_TYPE = SelectionType.MULTI;

/**
 * Represents the data we receive from the Hibu backend.
 */
export interface HibuFacetGroup {
    fieldName: string;
    values: HibuFacet[];
    errorCode?: HibuFacetErrorCode
}

export enum HibuFacetErrorCode {
    OVERFLOW_FACET_COUNT= 'OVERFLOW_FACET_COUNT',
}

export enum FieldType {
    STRING,
    DATE,
    DIRPATH,
}

export class FacetGroup {
    [immerable] = true;
    fieldName: string;
    fieldType: FieldType;
    hibuFacetValues: string[];
    collapsible: boolean;
    selectionType: SelectionType;
    showCounts: boolean;
    clearable: boolean;
    errorCode?: HibuFacetErrorCode;

    constructor(
        fieldName: string,
        fieldType: FieldType = FieldType.STRING,
        hibuFacetValues: string[] = [],
        collapsible = true,
        type = DEFAULT_SELECTION_TYPE,
        showCounts = true,
        clearable = true,
        errorCode = undefined
    ) {
        this.fieldName = fieldName;
        this.fieldType = fieldType;
        this.hibuFacetValues = hibuFacetValues;
        this.collapsible = collapsible;
        this.selectionType = type;
        this.showCounts = showCounts;
        this.clearable = clearable;
        this.errorCode = errorCode
    }
}

export type HibuFacet = {
    name: string; // translated value
    count: number;
    value: string; // key
    filter: HibuFilter;
};

export type HibuFilter = HibuRangeFilter | HibuEqualsFilter;

interface HibuFilterBase {
    type: string;
}

export class HibuRangeFilter implements HibuFilterBase {
    [immerable] = true;

    type: string;
    gte: string;
    lt: string;

    constructor(gte: string, lt: string) {
        this.type = 'range';
        this.gte = gte;
        this.lt = lt;
    }
}

export class HibuEqualsFilter implements HibuFilterBase {
    [immerable] = true;

    type: string;
    value: string;

    constructor(value: string) {
        this.type = 'equals';
        this.value = value;
    }
}

export type HibuFacetFilter = {
    [key: string]: HibuFilter[];
};

export type HibuFacetFilterFromUrl = {
    hibuFilters: HibuFacetFilter;
    filterValuesByFieldName: ReduxSelectedFacetValueByFieldName;
};

export enum FacetValuesSortOrder {
    COUNT = 'count',
    NAME = 'name',
    VALUE = 'value',
}

/** REDUX TYPES */
export type ReduxExpandedFacetGroups = {[key: string]: boolean};

export type ReduxHibuFacetValue = {[value: string]: HibuFacet};
export type ReduxHibuFacetValuesByFieldName = {[key: string]: ReduxHibuFacetValue};

export interface ReduxHibuFacetValues extends ReduxNormalizedType<ReduxHibuFacetValue> {
    byKey: ReduxHibuFacetValuesByFieldName;
    allKeys: string[];
}

export type ReduxFacetGroup = {[key: string]: FacetGroup};

export interface ReduxFacetGroups extends ReduxNormalizedType<FacetGroup> {
    byKey: ReduxFacetGroup;
    allKeys: string[];
}

export type ReduxSelectedFacetValueByFieldName = {[key: string]: string[]};

export interface ReduxSelectedFacetValuesByFieldName extends ReduxNormalizedType<string[]> {
    byKey: ReduxSelectedFacetValueByFieldName;
    allKeys: string[];
}

export type FacetState = {
    hibuFacetValues: ReduxHibuFacetValues;
    facetGroups: ReduxFacetGroups;
    expandedFacetGroups: ReduxExpandedFacetGroups;
    selectedFacetValuesByFieldName: ReduxSelectedFacetValuesByFieldName;
    facetValuesSortOrder: FacetValuesSortOrder | undefined;
};
